import { useMemo } from 'react';
import { GridCellProps } from 'react-virtualized';
import * as MultiGridStyles from '~/components/MultiGrid/styles';
import { ESTIMATE_ALL_FIELDS, ESTIMATE_FIELDS_ORDER } from './constants';
import { CELL_SETTINGS } from '~/components/MultiGrid/constants';
import HeaderContent from './HeaderContent';
import CellContent from './CellContent';
import { useEstimateListContext } from './hooks/useEstimateListContext';

export interface ICellContentProps {
  multiGridSettings: Readonly<{
    fixedRowCount: number;
    fixedColumnCount: number;
  }>;
}

interface IGridCellProps {
  gridProps: GridCellProps;
  cellProps: ICellContentProps;
}

const GridCell = ({ gridProps: { columnIndex, rowIndex, style }, cellProps }: IGridCellProps) => {
  const { listIds, listData, renderFilters } = useEstimateListContext();

  const thisEstimate = listData[listIds[rowIndex - 1]];

  const field = ESTIMATE_ALL_FIELDS[ESTIMATE_FIELDS_ORDER[columnIndex]];
  const keyName = field.value;

  const { multiGridSettings } = cellProps;

  const rowIndexType = useMemo(() => {
    if (rowIndex === 0) {
      return 'first';
    }
    return rowIndex === listIds.length ? 'last' : null;
  }, [rowIndex, listIds.length]);

  const columnIndexType = useMemo(() => {
    if (columnIndex === 0) {
      return 'first';
    }
    return columnIndex === ESTIMATE_FIELDS_ORDER.length - 1 ? 'last' : null;
  }, [columnIndex]);

  const isLastStaticColumn = useMemo(
    () => columnIndex === multiGridSettings.fixedColumnCount - 1,
    [columnIndex, multiGridSettings.fixedColumnCount],
  );

  if (rowIndex === 0) {
    return (
      <MultiGridStyles.HeaderCell
        columnIndexType={columnIndexType}
        style={style}
        {...{ [CELL_SETTINGS.lastColumnDataAttribute]: isLastStaticColumn }}
      >
        <HeaderContent
          fieldValue={field.value}
          fieldLabel={field.label}
          showFilter={renderFilters && (field.showFilter ?? false)}
          isSortable={renderFilters && (field.isSortable ?? false)}
        />
        {isLastStaticColumn && <MultiGridStyles.AbsoluteBorder />}
      </MultiGridStyles.HeaderCell>
    );
  }

  if (thisEstimate) {
    return (
      <MultiGridStyles.BodyCell
        className="align-center"
        rowIndexType={rowIndexType}
        columnIndexType={columnIndexType}
        paddingLayer={field.isCellFullWidth ? 'first_child' : 'self'}
        style={style}
        {...{ [CELL_SETTINGS.lastColumnDataAttribute]: isLastStaticColumn }}
      >
        <CellContent
          estimateReferenceId={thisEstimate.estimate_uuid}
          identifier={keyName}
          {...cellProps}
        />
        {isLastStaticColumn && <MultiGridStyles.AbsoluteBorder />}
      </MultiGridStyles.BodyCell>
    );
  }

  return null;
};

export default GridCell;
